import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setLoanAmmount,
  setTimePeriod,
  setIntrestRate,
  setBussinessEmi,
  setRemoveFields,
  resetBusinessData,
  setTotalAmount,
} from "../redux/calculations/bussinessSlice";
import {
  resetChartData,
  updatedBussinessChart,
} from "../redux/calculations/calculaterSlice";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

const BussinessCalc = () => {
  const dispatch = useDispatch();
  const { loanAmmount, intrestRate, timePeriod } = useSelector(
    (state) => state.bussinessCalculator
  );
  const [calcDone, setCalcDone] = useState(false);
  const [showTable, setShowTable] = useState(false);

  const { textData } = useLocale();
  const { bussinesCal } = textData;
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const [isFormModified, setIsFormModified] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      dispatch(setRemoveFields());
    };
  }, [dispatch]);
  const formik = useFormik({
    initialValues: {
      loanAmmount: loanAmmount,
      intrestRate: intrestRate,
      timePeriod: timePeriod,
    },
    validationSchema: Yup.object({
      loanAmmount: Yup.number()
        .min(10000, bussinesCal.loanAmountMin)
        .max(20000000, bussinesCal.loanAmountMax)
        .required(bussinesCal.loanAmountRequired),
      intrestRate: Yup.number()
        .min(5, bussinesCal.interestRateMin)
        .max(30, bussinesCal.interestRateMax)
        .required(bussinesCal.interestRateRequired),
      timePeriod: Yup.number()
        .min(1, bussinesCal.timePeriodMin)
        .max(30, bussinesCal.timePeriodMax)
        .required(bussinesCal.timePeriodRequired),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: async (values) => {
      const principal = parseFloat(values.loanAmmount);
      const rate = parseFloat(values.intrestRate) / 1200;
      const time = parseFloat(values.timePeriod) * 12;

      const bussinessEmi =
        (principal * rate * Math.pow(1 + rate, time)) /
        (Math.pow(1 + rate, time) - 1);
      const totalPayment = bussinessEmi * time;
      const totalInterest = totalPayment - principal;

      dispatch(setLoanAmmount(values.loanAmmount));
      dispatch(setIntrestRate(values.intrestRate));
      dispatch(setTimePeriod(values.timePeriod));
      dispatch(setBussinessEmi(bussinessEmi));

      dispatch(
        updatedBussinessChart({
          loanAmount: principal,
          totalInterest: totalInterest,
          bussinessEmi: bussinessEmi,
          totalPayment: totalPayment,
        })
      );
      saveToHistory({
        tabName: "businessLoanCalc",
        totalInvestment: principal,
        rate: rate,
        time: time,
        yearlyReturn: totalInterest,
        interestRate: values.intrestRate,
        tenure: values.timePeriod,
        emi: bussinessEmi,
        maturityAmount: totalPayment,
        calculationType: "Bussiness Loan",
      });
      dispatch(setTotalAmount(totalPayment));

      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_BUSSINESS");
      }
      setShowTable(false);
      setCalcDone(true);
      setIsFormModified(false);
    },
  });

  const saveToHistory = (calculationData) => {
    try {
      const existingData =
        JSON.parse(localStorage.getItem(textData.storeData)) || [];

      const newHistoryRecord = {
        tabName: calculationData.tabName,
        rate: calculationData.rate,
        time: calculationData.time,
        totalInvestment: calculationData.totalInvestment,
        yearlyReturn: calculationData.yearlyReturn,
        interestRate: calculationData.interestRate,
        tenure: calculationData.tenure,
        emi: calculationData.emi,
        maturityAmount: calculationData.maturityAmount,
        calculationType: calculationData.calculationType,
        calculationDate: new Date().toISOString(),
      };

      const isDuplicate = existingData.some((recordArray) => {
        if (!recordArray || !recordArray[0]) return false;
        const record = recordArray[0];

        return (
          record.calculationType === newHistoryRecord.calculationType &&
          record.totalInvestment === newHistoryRecord.totalInvestment &&
          record.interestRate === newHistoryRecord.interestRate &&
          record.tenure === newHistoryRecord.tenure &&
          record.maturityAmount === newHistoryRecord.maturityAmount &&
          record.emi === newHistoryRecord.emi
        );
      });

      if (!isDuplicate) {
        existingData.push([newHistoryRecord]);
        localStorage.setItem(textData.storeData, JSON.stringify(existingData));
      } else {
      }
    } catch (error) {}
  };

  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetBusinessData());
    };
  }, [dispatch]);

  const sliderTrackRef = useRef(null);
  const interestSliderRef = useRef(null);
  const periodSliderRef = useRef(null);

  const handleTimePeriodSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setTimePeriod(value));
    formik.setFieldValue("timePeriod", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleIntrestRatetSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setIntrestRate(value));
    formik.setFieldValue("intrestRate", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleLoanAmountSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setLoanAmmount(value));
    formik.setFieldValue("loanAmmount", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
          shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
        );
  };

  const handleTimeChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue.length > 2) return;
    formik.setFieldValue("timePeriod", rawValue);
    if (rawValue === "") return;
    const numericValue = Number(rawValue);
    if (!isNaN(numericValue)) {
      const clamped = Math.max(1, Math.min(30, numericValue));
      dispatch(setTimePeriod(clamped));
    }
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInterestRateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("intrestRate", rawValue);
      dispatch(setIntrestRate(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;

    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("intrestRate", normalizedValue);
    dispatch(setIntrestRate(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };
  const handleInvestmentChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.,]/g, "");

    if (rawValue.length > 9) return;

    if (rawValue === "" || rawValue === "0") {
      formik.setFieldValue("loanAmmount", "");
      dispatch(setLoanAmmount(""));
      return;
    }
    if (rawValue.match(/^[.,]+$/)) {
      // Don't update the field values, just return
      return;
    }

    const numericValue = parseFloat(
      rawValue
        .replace(/,/g, ".")
        .replace(/\./g, "")
        .replace(/(\d+)(\.\d{2})?/, "$1.$2")
    ); // Convert to a number

    const formattedValue = new Intl.NumberFormat(i18n.language).format(
      numericValue
    );
    formik.setFieldValue("loanAmmount", numericValue);
    dispatch(setLoanAmmount(numericValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  useEffect(() => {
    dispatch(setLoanAmmount(formik.values.loanAmmount));
    dispatch(setTimePeriod(formik.values.timePeriod));
    dispatch(setIntrestRate(formik.values.intrestRate));
  }, [formik.values, dispatch]);

  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {textData.bussinessContent.heading}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.bussinessContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {textData.bussinessContent.howItWorksHeading}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.bussinessContent.howItWorksDesc}
      </p>

      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        {textData.bussinessContent.formula}
      </p>

      <table className="border-collapse mt-4 w-full">
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              P
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.bussinessContent.symbolTable.symbols.P}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              R
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.bussinessContent.symbolTable.symbols.R}
            </td>
          </tr>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              N
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.bussinessContent.symbolTable.symbols.N}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {textData.bussinessContent.benefitsHeading}
      </h2>
      <ul
        className={`list-disc ${
          isRTL() ? "pr-5" : "pl-5"
        }  leading-relaxed mt-3 text-sm sm:text-[15px]`}
      >
        <li>{textData.bussinessContent.benefitsList[0]}</li>
        <li>{textData.bussinessContent.benefitsList[1]}</li>

        <li>{textData.bussinessContent.benefitsList[2]}</li>

        <li>{textData.bussinessContent.benefitsList[3]}</li>

        <li>{textData.bussinessContent.benefitsList[4]}</li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {textData.bussinessContent.exampleHeading}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.bussinessContent.exampleDesc}
      </p>
      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {textData.bussinessContent.amortizationHeading}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.bussinessContent.amortizationDesc}
      </p>
    </div>
  );
  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };
  // const timeLabel =t("year").charAt(0).toUpperCase()
  const timeLabel = t("yearShort");

  return (
    <>
      <Helmet>
        <title>Business Loan EMI Calculator – Calculate in Seconds</title>
        <meta
          name="description"
          content="Quickly calculate your business line of credit with our smart Business Loan EMI Calculator. Plan EMIs, manage cash flow, and grow your business with ease."
        />
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/business-loan-calculator"
        />

        <meta
          property="og:title"
          content="Business Loan EMI Calculator for Smart Entrepreneurs"
        />
        <meta
          property="og:description"
          content="Quickly calculate your business line of credit with our smart Business Loan EMI Calculator. Plan EMIs, manage cash flow, and grow your business with ease."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/business-loan-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="Get Business Line of Credit Made Easy - GrowLifeFund"
        />

        <meta name="robots" content="index, follow" />
      </Helmet>
      <CalculatorLayout
        title={
          <>
            {" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.your}{" "}
            </span>
            {textData.bussinesCal.titleMain}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.emis}
            </span>
          </>
        }
        description={textData.bussinesCal.description}
        descriptionContent={descriptionContent}
        setShowTable={setShowTable}
        showTable={showTable}
        calcDone={calcDone}
        setCalcDone={setCalcDone}
        isFormModified={isFormModified}
        setIsFormModified={setIsFormModified}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.loanAmount}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative  w-full" ref={sliderTrackRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={10000}
                    max={20000000}
                    step={1000}
                    value={loanAmmount}
                    onChange={handleLoanAmountSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                        right: 0,
                        left: "auto",
                        transform: "scaleX(-1)",
                      }),
                      "--val": `${
                        ((loanAmmount - 10000) / (20000000 - 10000)) * 100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="loanAmmount"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.loanAmmount)}
                    onChange={handleInvestmentChange}
                    onBlur={formik.handleBlur}
                    dir="ltr"
                    type="text"
                    placeholder={getPlaceholderValue(i18n.language)}
                    // onChange={handleLoanAmountChange}
                    borderColor={
                      formik.touched.loanAmmount && formik.errors.loanAmmount
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px] border ${
                      formik.touched.loanAmmount && formik.errors.loanAmmount
                        ? "border-danger"
                        : "border-gray-light"
                    }    flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                    style={{ direction: isRTL() ? "rtl" : "ltr" }}
                  />
                  {formik.touched.loanAmmount && formik.errors.loanAmmount && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.loanAmmount}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.rateOfInterest}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative  w-full" ref={interestSliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={5}
                    max={30}
                    step={0.1}
                    value={parseFloat(intrestRate)}
                    onChange={handleIntrestRatetSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((intrestRate - 5) / (30 - 5)) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="intrestRate"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("6.5")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.intrestRate
                    )}
                    onChange={handleInterestRateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.intrestRate && formik.errors.intrestRate
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px] border ${
                      formik.touched.intrestRate && formik.errors.intrestRate
                        ? "border-danger"
                        : "border-gray-light"
                    } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.intrestRate && formik.errors.intrestRate && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.intrestRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.timePeriod}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative  w-full" ref={periodSliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={1}
                    max={30}
                    step={1}
                    value={timePeriod}
                    onChange={handleTimePeriodSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((timePeriod - 1) / (30 - 1)) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="timePeriod"
                    leftIcon={timeLabel}
                    value={formik.values.timePeriod}
                    onChange={handleTimeChange}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={"5"}
                    borderColor={
                      formik.touched.timePeriod && formik.errors.timePeriod
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px] border ${
                      formik.touched.timePeriod && formik.errors.timePeriod
                        ? "border-danger"
                        : "border-gray-light"
                    } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.timePeriod && formik.errors.timePeriod && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.timePeriod}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px]">
              {" "}
              <Button type="submit" className="w-full">
                {textData.calculate}
              </Button>
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default BussinessCalc;
